import type {
  AuthResponse,
  UserProfile,
  Dashboard,
  ProjectAnalytics,
  Expense,
  Project,
  CreateProjectInput,
  UpdateProjectInput,
  CreateExpenseInput,
  UpdateExpenseInput,
  Stage,
  CreateStageInput,
  UpdateStageInput,
  StageItem,
  StageItemStatus,
  CreateStageItemInput,
  UpdateStageItemInput,
  StageTemplatePreview,
  SupportRequest,
  ProjectMember,
  ProjectMemberRole,
  ProjectInvitation,
  CreateInvitationInput,
  InvitationInfo,
  TelegramOAuthData,
  TelegramLoginData,
  TelegramLoginOptions,
  CurrencyCode,
  Locale,
  Need,
  CreateNeedInput,
  UpdateNeedInput,
  NeedOption,
  CreateNeedOptionInput,
  UpdateNeedOptionInput,
  ProductLinkPreview,
  NeedTemplatePreview,
  BudgetPlanSummary,
  UpdateBudgetPlanInput,
  AiBudgetSuggestResponse,
  AiFloorPlanTraceResponse,
  ExpenseCategoryDto,
  WeeklyReportSubscription,
  WeeklyReportFeedItem,
  ActiveAnnouncementsResponse,
  StoryDto,
  StorySeenResponse,
  StoryVotesResponse,
  NotificationSettingsResponse,
  NotificationPreference,
  PushSubscriptionInput,
  Attachment,
  AttachmentKind,
  AttachmentUploadTicket,
  CreateAttachmentUploadInput,
  FloorPlan,
  CreateFloorPlanInput,
  UpdateFloorPlanInput,
} from '@etap/shared';
import { normalizeScreen } from '@etap/shared';

import { trackAppEvent } from './appEvents';

export type KanbanItemWithStage = StageItem & {
  stage: { id: string; title: string; status: string; order: number };
};

export type KanbanBoard = Record<StageItemStatus, KanbanItemWithStage[]>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

function getClientSource(): 'telegram' | 'webapp' {
  const tg = window.Telegram?.WebApp;
  return tg && tg.initData ? 'telegram' : 'webapp';
}

/** Сервер отверг сессию (refresh-токена нет или он недействителен) — только тогда разлогиниваем. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired. Please login again.');
    this.name = 'SessionExpiredError';
  }
}

class ApiClient {
  private token: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<string> | null = null;

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('etap_token', token);
  }

  getToken(): string | null {
    if (!this.token) {
      this.token = localStorage.getItem('etap_token');
    }
    return this.token;
  }

  clearToken() {
    this.token = null;
    localStorage.removeItem('etap_token');
  }

  private async refreshAccessToken(): Promise<string> {
    // Prevent concurrent refresh requests
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include', // Отправляем cookies
          headers: {
            'X-Client-Source': getClientSource(),
          },
        });

        // 429/5xx/сбой сети — не повод выкидывать человека из аккаунта
        if (response.status === 401) {
          throw new SessionExpiredError();
        }
        if (!response.ok) {
          throw new Error(`Refresh failed: HTTP ${response.status}`);
        }

        const data = await response.json();
        this.setToken(data.accessToken);
        return data.accessToken;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const headers: Record<string, string> = {
      'X-Client-Source': getClientSource(),
      ...options.headers as Record<string, string>,
    };

    // Content-Type ставим только для JSON-строк (все вызовы через request()
    // так и шлют тело). FormData/Blob должны выставить multipart-границу
    // сами — этот класс их не отправляет (см. uploadToPresignedUrl ниже),
    // но проверка типа защищает от будущей ошибки, если кто-то попробует.
    if (typeof options.body === 'string') {
      headers['Content-Type'] = 'application/json';
    }

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Первая попытка запроса
    let response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Отправляем/получаем cookies
    });

    // Если получили 401, пытаемся обновить токен
    // Login failures belong to the provider, not to an expired app session.
    const isLoginRequest = endpoint.startsWith('/auth/telegram') ||
      ['/auth/yandex/callback', '/auth/apple', '/auth/dev'].includes(endpoint);
    if (response.status === 401 && !isLoginRequest && !endpoint.includes('/auth/refresh')) {
      try {
        // Пытаемся обновить access token через refresh token
        const newToken = await this.refreshAccessToken();

        // Повторяем запрос с новым токеном
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
        });
      } catch (refreshError) {
        // Токен чистим только если сервер действительно отверг сессию
        if (refreshError instanceof SessionExpiredError) this.clearToken();
        throw refreshError;
      }

      // Свежий токен и всё равно 401 — сессию отвергли (например, пользователь удалён)
      if (response.status === 401) {
        this.clearToken();
        throw new SessionExpiredError();
      }
    }

    if (!response.ok) {
      const body = await response.json().catch(() => ({ error: 'Unknown error' }));
      const err = new Error(body.error || `HTTP ${response.status}`);
      if (body.nextAvailableAt) Object.assign(err, { nextAvailableAt: body.nextAvailableAt });

      // Трение, которое иначе видно только в логах сервера — и там его не
      // связать с человеком. Путь нормализуем: /projects/cmk123/expenses
      // превратится в /projects/:id/expenses, иначе группировать нечем.
      // Аналитику собственных сбоев не шлём, чтобы не зациклиться.
      if (!endpoint.startsWith('/tracking/')) {
        trackAppEvent('api_error', {
          endpoint: normalizeScreen(endpoint.split('?')[0]),
          method: (options.method || 'GET').toUpperCase(),
          status: response.status,
        });
      }

      throw err;
    }

    return response.json();
  }

  // Auth
  async getTelegramLoginOptions(): Promise<TelegramLoginOptions> {
    return this.request<TelegramLoginOptions>('/auth/telegram/login/options', { signal: AbortSignal.timeout(10_000) });
  }

  async authenticateWithTelegramLogin(data: TelegramLoginData, timeZone?: string, wuid?: string, locale?: Locale): Promise<AuthResponse> {
    if (!('id_token' in data)) return this.authenticateWithTelegramOAuth(data, timeZone, wuid, locale);
    const result = await this.request<AuthResponse>('/auth/telegram/login', {
      method: 'POST',
      body: JSON.stringify({ idToken: data.id_token, timeZone, source: 'webapp', wuid, locale }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  async authenticateWithTelegram(initData: string, timeZone?: string, wuid?: string, locale?: Locale): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ initData, timeZone, source: 'telegram', wuid, locale }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  async authenticateWithTelegramOAuth(authData: TelegramOAuthData, timeZone?: string, wuid?: string, locale?: Locale): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/telegram/oauth', {
      method: 'POST',
      body: JSON.stringify({ authData, timeZone, source: 'webapp', wuid, locale }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  async authenticateWithYandex(
    code: string,
    state: string,
    timeZone?: string,
    locale?: Locale,
    wuid?: string,
  ): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/yandex/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state, timeZone, source: 'webapp', locale, wuid }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  // Dev auth (только для разработки)
  async authenticateDev(timeZone?: string, locale?: Locale): Promise<AuthResponse> {
    const result = await this.request<AuthResponse>('/auth/dev', {
      method: 'POST',
      body: JSON.stringify({ timeZone, source: 'webapp', locale }),
    });
    this.setToken(result.accessToken);
    return result;
  }

  // Получение текущего пользователя по токену
  async getMe(): Promise<{ user: UserProfile }> {
    return this.request<{ user: UserProfile }>('/auth/me');
  }

  // Обновление настроек пользователя
  async updateMe(data: { timeZone?: string; locale?: Locale; defaultCurrency?: CurrencyCode }): Promise<{ user: UserProfile }> {
    return this.request<{ user: UserProfile }>('/auth/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Настройки еженедельного отчёта по конкретному проекту
  async getWeeklyReport(projectId: string): Promise<WeeklyReportSubscription> {
    return this.request<WeeklyReportSubscription>(`/projects/${projectId}/weekly-report`);
  }

  async updateWeeklyReport(
    projectId: string,
    data: { enabled?: boolean; day?: number },
  ): Promise<WeeklyReportSubscription> {
    return this.request<WeeklyReportSubscription>(`/projects/${projectId}/weekly-report`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Отправить отчёт по проекту сейчас
  async sendReport(projectId: string): Promise<{ success: boolean; projectCount: number; nextAvailableAt: string }> {
    return this.request<{ success: boolean; projectCount: number; nextAvailableAt: string }>(
      `/projects/${projectId}/weekly-report/send`,
      { method: 'POST' },
    );
  }

  // Лента (история) отчётов. projectId ограничивает выборку проектом.
  async getReports(projectId?: string, limit = 50): Promise<WeeklyReportFeedItem[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    if (projectId) params.set('projectId', projectId);
    return this.request<WeeklyReportFeedItem[]>(`/reports?${params.toString()}`);
  }

  async getUnreadReportsCount(projectId?: string): Promise<{ count: number }> {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return this.request<{ count: number }>(`/reports/unread-count${qs}`);
  }

  async markReportRead(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/reports/${id}/read`, { method: 'POST' });
  }

  async markAllReportsRead(projectId?: string): Promise<{ success: boolean; updated: number }> {
    const qs = projectId ? `?projectId=${encodeURIComponent(projectId)}` : '';
    return this.request<{ success: boolean; updated: number }>(`/reports/read-all${qs}`, { method: 'POST' });
  }

  // Stories (истории на дашборде)
  async getActiveAnnouncements(): Promise<ActiveAnnouncementsResponse> {
    return this.request<ActiveAnnouncementsResponse>('/announcements/active');
  }

  /** Архив анонсов: всё доступное, включая прочитанное. Показы не отмечает. */
  async getAllAnnouncements(): Promise<ActiveAnnouncementsResponse> {
    return this.request<ActiveAnnouncementsResponse>('/announcements/all');
  }

  async markAnnouncementSeen(
    key: string,
    payload: { closed?: boolean; cta?: boolean } = {},
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      `/announcements/${encodeURIComponent(key)}/seen`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
  }

  async getStories(): Promise<StoryDto[]> {
    return this.request<StoryDto[]>('/stories');
  }

  async getSeenStories(): Promise<StorySeenResponse> {
    return this.request<StorySeenResponse>('/stories/seen');
  }

  async markStoriesSeen(keys: string[], projectId?: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/stories/seen', {
      method: 'POST',
      body: JSON.stringify({ keys, ...(projectId ? { projectId } : {}) }),
    });
  }

  async getPollVotes(): Promise<StoryVotesResponse> {
    return this.request<StoryVotesResponse>('/stories/votes');
  }

  async votePoll(
    key: string,
    questionId: string,
    optionId: string,
    projectId?: string,
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/stories/${encodeURIComponent(key)}/vote`, {
      method: 'POST',
      body: JSON.stringify({ questionId, optionId, ...(projectId ? { projectId } : {}) }),
    });
  }

  // Projects
  async getProjects(): Promise<Project[]> {
    return this.request<Project[]>('/projects');
  }

  async getActiveProject(): Promise<{ projectId: string | null }> {
    return this.request<{ projectId: string | null }>('/projects/active');
  }

  async setActiveProject(projectId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/projects/active', {
      method: 'POST',
      body: JSON.stringify({ projectId }),
    });
  }

  async createProject(data: CreateProjectInput): Promise<Project> {
    return this.request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProject(id: string): Promise<Project> {
    return this.request<Project>(`/projects/${id}`);
  }

  async updateProject(id: string, data: UpdateProjectInput): Promise<Project> {
    return this.request<Project>(`/projects/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  // Dashboard
  async getDashboard(projectId: string): Promise<Dashboard> {
    return this.request<Dashboard>(`/projects/${projectId}/dashboard`);
  }

  async getProjectAnalytics(projectId: string): Promise<ProjectAnalytics> {
    return this.request<ProjectAnalytics>(`/projects/${projectId}/analytics`);
  }

  // Expenses
  async getExpenses(projectId: string, params?: {
    limit?: number;
    offset?: number;
    status?: string;
    category?: string;
    paymentMethod?: string;
    payeeType?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<{ data: Expense[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.offset) query.set('offset', String(params.offset));
    if (params?.status) query.set('status', params.status);
    if (params?.category) query.set('category', params.category);
    if (params?.paymentMethod) query.set('paymentMethod', params.paymentMethod);
    if (params?.payeeType) query.set('payeeType', params.payeeType);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);

    const queryString = query.toString();
    return this.request<{ data: Expense[]; total: number }>(
      `/projects/${projectId}/expenses${queryString ? `?${queryString}` : ''}`
    );
  }

  async createExpense(projectId: string, data: CreateExpenseInput): Promise<Expense> {
    return this.request<Expense>(`/projects/${projectId}/expenses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getExpense(projectId: string, expenseId: string): Promise<Expense> {
    return this.request<Expense>(`/projects/${projectId}/expenses/${expenseId}`);
  }

  async updateExpense(projectId: string, expenseId: string, data: Partial<UpdateExpenseInput>): Promise<Expense> {
    return this.request<Expense>(`/projects/${projectId}/expenses/${expenseId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteExpense(projectId: string, expenseId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${projectId}/expenses/${expenseId}`, {
      method: 'DELETE',
    });
  }

  // Attachments (сметы, фото, чеки). Байты идут мимо этого класса —
  // см. uploadToPresignedUrl() ниже, вне ApiClient.
  async createAttachmentUpload(projectId: string, data: CreateAttachmentUploadInput): Promise<AttachmentUploadTicket> {
    return this.request<AttachmentUploadTicket>(`/projects/${projectId}/attachments/upload-url`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async confirmAttachments(projectId: string, ids: string[]): Promise<Attachment[]> {
    return this.request<Attachment[]>(`/projects/${projectId}/attachments/confirm`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    });
  }

  async attachAttachmentsToExpense(projectId: string, ids: string[], expenseId: string): Promise<{ attached: number }> {
    return this.request<{ attached: number }>(`/projects/${projectId}/attachments/attach`, {
      method: 'POST',
      body: JSON.stringify({ ids, expenseId }),
    });
  }

  async getAttachment(projectId: string, attachmentId: string): Promise<Attachment> {
    return this.request<Attachment>(`/projects/${projectId}/attachments/${attachmentId}`);
  }

  async getAttachments(projectId: string, params?: { kind?: AttachmentKind; expenseId?: string | null }): Promise<Attachment[]> {
    const query = new URLSearchParams();
    if (params?.kind) query.set('kind', params.kind);
    if (params?.expenseId !== undefined) query.set('expenseId', params.expenseId === null ? 'null' : params.expenseId);
    const qs = query.toString();
    return this.request<Attachment[]>(`/projects/${projectId}/attachments${qs ? `?${qs}` : ''}`);
  }

  async deleteAttachment(projectId: string, attachmentId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${projectId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  }

  // Планировки (2D-редактор)
  async getFloorPlans(projectId: string): Promise<FloorPlan[]> {
    return this.request<FloorPlan[]>(`/projects/${projectId}/floor-plans`);
  }

  async getFloorPlan(projectId: string, planId: string): Promise<FloorPlan> {
    return this.request<FloorPlan>(`/projects/${projectId}/floor-plans/${planId}`);
  }

  async createFloorPlan(projectId: string, data: CreateFloorPlanInput): Promise<FloorPlan> {
    return this.request<FloorPlan>(`/projects/${projectId}/floor-plans`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateFloorPlan(projectId: string, planId: string, data: UpdateFloorPlanInput): Promise<FloorPlan> {
    return this.request<FloorPlan>(`/projects/${projectId}/floor-plans/${planId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteFloorPlan(projectId: string, planId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${projectId}/floor-plans/${planId}`, {
      method: 'DELETE',
    });
  }

  async traceFloorPlanPhoto(
    projectId: string,
    planId: string,
    attachmentId: string,
    aspect: number,
    image?: string,
  ): Promise<AiFloorPlanTraceResponse> {
    return this.request<AiFloorPlanTraceResponse>(`/projects/${projectId}/floor-plans/${planId}/ai/trace-photo`, {
      method: 'POST',
      body: JSON.stringify({ attachmentId, aspect, image }),
    });
  }

  async getExpenseCategories(projectType?: string): Promise<ExpenseCategoryDto[]> {
    const query = projectType ? `?projectType=${encodeURIComponent(projectType)}` : '';
    return this.request<ExpenseCategoryDto[]>(`/expense-categories${query}`);
  }

  async splitExpense(
    projectId: string,
    expenseId: string,
    data: { paidAmount: number; date?: string }
  ): Promise<{ parent: Expense; child: Expense }> {
    return this.request<{ parent: Expense; child: Expense }>(
      `/projects/${projectId}/expenses/${expenseId}/split`,
      { method: 'POST', body: JSON.stringify(data) }
    );
  }

  // Stages
  async getStageTemplates(): Promise<StageTemplatePreview[]> {
    return this.request<StageTemplatePreview[]>('/projects/stages/templates');
  }

  async getStages(projectId: string): Promise<Stage[]> {
    return this.request<Stage[]>(`/projects/${projectId}/stages`);
  }

  async createStage(projectId: string, data: CreateStageInput): Promise<Stage> {
    return this.request<Stage>(`/projects/${projectId}/stages`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async applyStageTemplate(
    projectId: string,
    templateId: string,
    selectedStageIndexes?: number[]
  ): Promise<Stage[]> {
    return this.request<Stage[]>(`/projects/${projectId}/stages/apply-template`, {
      method: 'POST',
      body: JSON.stringify({ templateId, selectedStageIndexes }),
    });
  }

  async updateStage(stageId: string, data: UpdateStageInput): Promise<Stage> {
    return this.request<Stage>(`/projects/stages/${stageId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStage(stageId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/stages/${stageId}`, {
      method: 'DELETE',
    });
  }

  // Stage Items
  async getStageItems(stageId: string): Promise<StageItem[]> {
    return this.request<StageItem[]>(`/projects/stages/${stageId}/items`);
  }

  async createStageItem(stageId: string, data: CreateStageItemInput): Promise<StageItem> {
    return this.request<StageItem>(`/projects/stages/${stageId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateStageItem(itemId: string, data: UpdateStageItemInput): Promise<StageItem> {
    return this.request<StageItem>(`/projects/stages/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteStageItem(itemId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/stages/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async getKanbanBoard(projectId: string): Promise<KanbanBoard> {
    return this.request<KanbanBoard>(`/projects/${projectId}/kanban`);
  }

  // Support
  async createSupportRequest(data: { message: string; contact?: string; source?: 'WEBAPP' | 'BOT' }) {
    return this.request<SupportRequest>('/support', {
      method: 'POST',
      body: JSON.stringify({ ...data, source: data.source || 'WEBAPP' }),
    });
  }

  async getSupportRequests(): Promise<SupportRequest[]> {
    return this.request<SupportRequest[]>('/support');
  }

  // Project Members
  async getProjectMembers(projectId: string): Promise<ProjectMember[]> {
    return this.request<ProjectMember[]>(`/projects/${projectId}/members`);
  }

  async updateMemberRole(
    projectId: string,
    memberId: string,
    role: 'EDITOR' | 'VIEWER'
  ): Promise<ProjectMember> {
    return this.request<ProjectMember>(`/projects/${projectId}/members/${memberId}`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async removeMember(projectId: string, memberId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${projectId}/members/${memberId}`, {
      method: 'DELETE',
    });
  }

  async leaveProject(projectId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/${projectId}/members/me`, {
      method: 'DELETE',
    });
  }

  // Project Invitations
  async createInvitation(
    projectId: string,
    data: CreateInvitationInput
  ): Promise<ProjectInvitation> {
    return this.request<ProjectInvitation>(`/projects/${projectId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProjectInvitations(projectId: string): Promise<ProjectInvitation[]> {
    return this.request<ProjectInvitation[]>(`/projects/${projectId}/invitations`);
  }

  async revokeInvitation(
    projectId: string,
    invitationId: string
  ): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(
      `/projects/${projectId}/invitations/${invitationId}`,
      { method: 'DELETE' }
    );
  }

  // Public invitation info (no auth required but we send token anyway)
  async getInvitationInfo(token: string): Promise<InvitationInfo> {
    return this.request<InvitationInfo>(`/invitations/${token}`);
  }

  async acceptInvitation(token: string): Promise<{ projectId: string; role: ProjectMemberRole }> {
    return this.request<{ projectId: string; role: ProjectMemberRole }>(
      `/invitations/${token}/accept`,
      { method: 'POST' }
    );
  }

  // Needs (Materials)
  async getNeedTemplates(): Promise<NeedTemplatePreview[]> {
    return this.request<NeedTemplatePreview[]>('/projects/needs/templates');
  }

  async getNeeds(
    projectId: string,
    params?: { status?: string; category?: string }
  ): Promise<Need[]> {
    const query = new URLSearchParams();
    if (params?.status) query.set('status', params.status);
    if (params?.category) query.set('category', params.category);
    const queryString = query.toString();
    return this.request<Need[]>(
      `/projects/${projectId}/needs${queryString ? `?${queryString}` : ''}`
    );
  }

  async getNeed(projectId: string, needId: string): Promise<Need> {
    return this.request<Need>(`/projects/${projectId}/needs/${needId}`);
  }

  async createNeed(projectId: string, data: CreateNeedInput): Promise<Need> {
    return this.request<Need>(`/projects/${projectId}/needs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async applyNeedTemplate(
    projectId: string,
    templateId: string,
    selectedItemIndexes?: number[]
  ): Promise<Need[]> {
    return this.request<Need[]>(`/projects/${projectId}/needs/apply-template`, {
      method: 'POST',
      body: JSON.stringify({ templateId, selectedItemIndexes }),
    });
  }

  async updateNeed(needId: string, data: UpdateNeedInput): Promise<Need> {
    return this.request<Need>(`/projects/needs/${needId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteNeed(needId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/needs/${needId}`, {
      method: 'DELETE',
    });
  }

  // Need Options
  async createNeedOption(needId: string, data: CreateNeedOptionInput): Promise<NeedOption> {
    return this.request<NeedOption>(`/projects/needs/${needId}/options`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getProductLinkPreview(url: string): Promise<ProductLinkPreview> {
    return this.request<ProductLinkPreview>('/projects/needs/link-preview', {
      method: 'POST',
      body: JSON.stringify({ url }),
    });
  }

  async updateNeedOption(optionId: string, data: UpdateNeedOptionInput): Promise<NeedOption> {
    return this.request<NeedOption>(`/projects/needs/options/${optionId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async reorderNeedOptions(needId: string, ids: string[]): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/needs/${needId}/options/order`, {
      method: 'PUT',
      body: JSON.stringify({ ids }),
    });
  }

  async deleteNeedOption(optionId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/projects/needs/options/${optionId}`, {
      method: 'DELETE',
    });
  }

  async selectNeedOption(optionId: string): Promise<NeedOption> {
    return this.request<NeedOption>(`/projects/needs/options/${optionId}/select`, {
      method: 'PATCH',
    });
  }

  async deselectNeedOption(optionId: string): Promise<NeedOption> {
    return this.request<NeedOption>(`/projects/needs/options/${optionId}/deselect`, {
      method: 'PATCH',
    });
  }

  // === Budget ===

  async getBudgetPlan(projectId: string): Promise<BudgetPlanSummary> {
    return this.request<BudgetPlanSummary>(`/projects/${projectId}/budget`);
  }

  async updateBudgetPlan(
    projectId: string,
    data: UpdateBudgetPlanInput
  ): Promise<BudgetPlanSummary> {
    return this.request<BudgetPlanSummary>(`/projects/${projectId}/budget`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async distributeRemainder(projectId: string): Promise<BudgetPlanSummary> {
    return this.request<BudgetPlanSummary>(`/projects/${projectId}/budget/distribute-remainder`, {
      method: 'POST',
    });
  }

  // Logout - отзывает текущий refresh token
  async logout(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
          'X-Client-Source': getClientSource(),
        },
        credentials: 'include',
      });
    } catch (err) {
      // Ignore errors
    } finally {
      this.clearToken();
    }
  }

  async getAiBudgetSuggestion(
    projectId: string,
    description?: string,
  ): Promise<AiBudgetSuggestResponse> {
    return this.request(`/projects/${projectId}/ai/budget-suggest`, {
      method: 'POST',
      body: JSON.stringify({ description }),
    });
  }

  // ─── Уведомления ────────────────────────────────────────────────────────────

  async getNotificationSettings(): Promise<NotificationSettingsResponse> {
    return this.request('/notifications/settings');
  }

  async updateNotificationSettings(preferences: NotificationPreference[]): Promise<{ success: boolean }> {
    return this.request('/notifications/settings', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    });
  }

  async subscribeToPush(subscription: PushSubscriptionInput): Promise<{ success: boolean }> {
    return this.request('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    });
  }

  async unsubscribeFromPush(endpoint: string): Promise<{ success: boolean }> {
    return this.request('/notifications/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    });
  }

  // Logout-all - отзывает все refresh tokens пользователя (выход со всех устройств)
  async logoutAll(): Promise<void> {
    try {
      await fetch(`${API_BASE_URL}/auth/logout-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getToken()}`,
          'X-Client-Source': getClientSource(),
        },
        credentials: 'include',
      });
    } catch (err) {
      // Ignore errors
    } finally {
      this.clearToken();
    }
  }
}

export const api = new ApiClient();

/**
 * Best-effort удаление вложения при уходе со страницы (unmount-guard формы
 * расхода: пользователь прикрепил чек и ушёл назад, не сохранив). fetch с
 * keepalive переживает саму навигацию — обычный fetch/XHR браузер может
 * оборвать. Ошибку намеренно игнорируем: настоящая страховка — часовой GC
 * на сервере (зависшие/непривязанные вложения), здесь только попытка
 * убрать мусор пораньше.
 */
export function deleteAttachmentKeepalive(projectId: string, attachmentId: string): void {
  const token = api.getToken();
  if (!token) return;
  fetch(`${API_BASE_URL}/projects/${projectId}/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}`, 'X-Client-Source': getClientSource() },
    keepalive: true,
  }).catch(() => {});
}

/**
 * Загрузка файла по предподписанной ссылке — мимо ApiClient.request(), и это
 * не обход правил, а требование:
 *  - Authorization/X-Client-Source в этом запросе не нужны и только
 *    ужесточают CORS-preflight на файловом хосте (Garage) без причины;
 *  - credentials: 'omit' — незачем отправлять refresh-cookie на файловый хост;
 *  - fetch не даёт прогресс загрузки, а для крупных фото на мобильной сети
 *    он нужен для UI — поэтому здесь XHR, а не fetch.
 * Подпись сама себе авторизация: она живёт несколько минут и покрывает
 * ровно один ключ объекта — токен пользователя здесь ни при чём, поэтому
 * 401/протухший access token в середине загрузки её не ломает.
 */
export function uploadToPresignedUrl(
  ticket: AttachmentUploadTicket,
  body: Blob,
  onProgress?: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', ticket.uploadUrl, true);
    for (const [key, value] of Object.entries(ticket.headers)) {
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: HTTP ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort());
    }
    xhr.send(body);
  });
}
